import { xUnits } from '../../UNSAFE_Theme';

// The Size type is used for gap and dimensions (e.g., width/height) and padding and similar
// properties. Using the sizeToCSS function below we transform the property of this type into
// something that is usable in a css style property.
export type Size =
  | 0
  | `calc(${string})`
  | `var(${string})`
  | `--${string}`
  | `${number}x`
  | `${number}%`
  | `${number}${CssUnits}`;

type CssUnits = (typeof cssUnits)[number];

// list of supported length units https://www.w3schools.com/cssref/css_units.php
const cssUnits = [
  'ch',
  'cm',
  'mm',
  'in',
  'pc',
  'pt',
  'px',
  'em',
  'ex',
  'rem',
  'vh',
  'vw',
  'vmin',
  'vmax'
] as const;

const cssUnitRegex = new RegExp(`^-?\\d*\\.?\\d+(${cssUnits.join('|')})$`);
const xUnitRegex = new RegExp(`^-?\\d*\\.?\\d+x$`);

const isStringZero = (v: string) => parseFloat(v) === 0;
const isPercent = (v: string) => v.endsWith('%');
const isCalcExpression = (v: string) => v.startsWith('calc(') && v.endsWith(')');
const isVarFunction = (v: string) => v.startsWith('var(') && v.endsWith(')');
const isCssVar = (v: string) => v.startsWith('--');
const isCssUnit = (v: string) => cssUnitRegex.test(v);
const isXUnit = (v: string) => xUnitRegex.test(v);

/**
 * This transforms a number + 'x' to a css calc.
 * @param {string } v - a value to transform
 * @returns {string | 0}
 */
const xToCSS = (v: string) => {
  // parse out the 'x'
  const nStr = v.slice(0, v.length - 1);
  // nStr should now only contain characters that could be part of a floating number.
  // I don't want a string with random characters in it to be ignored as it would with
  // parseFloat, so I'm using Number here. parseFloat('1.2p') -> 1.2. whereas
  // Number('1.2p') returns NaN.
  const num = Number(nStr);
  if (isNaN(num)) return 0;

  // Note: 1x is equal to about 4px
  return xUnits(num);
};

/**
 * Given a value that is of type Size, transform the value into
 * something that is usable in a css style property.
 * @param {string | 0 } v - a value to transform
 * @returns {string | 0}
 */
export const sizeToCSS = (v: Size) => {
  return v === 0 || isStringZero(v) // We want 0, '0%' and '0x' to return 0
    ? 0
    : isVarFunction(v) || isCalcExpression(v) || isPercent(v) || isCssUnit(v)
    ? v
    : isCssVar(v)
    ? `var(${v})`
    : isXUnit(v)
    ? xToCSS(v)
    : 0; // return 0 for any invalid v.
};
